import type { AssetRopaDpiaFilterFields } from 'config/api/assetRopa/assetRopaDpiaFilters';
import {
  DPIA_LIFECYCLE_STATUS_FILTER_VALUES,
  DPIA_REQUEST_STATUS_FILTER_VALUES,
  isDpiaIndicationYes,
} from 'config/api/assetRopa/assetRopaDpiaFilters';
import processing_activities_messages from 'messages/processing_activities_messages';

export type ApaDpiaDrawerFilterChip = {
  id: string;
  name: string | string[];
};

export type ApaDpiaDrawerFilterSection = {
  sectionName: string[];
  filters: ApaDpiaDrawerFilterChip[];
  isSingleSelect: boolean;
  isDisabled?: boolean;
  disabledTooltip?: string[];
};

const dpiaDependentFiltersDisabledTooltip = processing_activities_messages.asset_ropa.filters.dpia_dependent_filters_hint;

const booleanFilterMessage = (value: 'true' | 'false'): string | string[] =>
  processing_activities_messages.asset_ropa.filters[value === 'true' ? 'yes' : 'no'];

const requestStatusMessage = (value: string): string | string[] => {
  const messageMap = processing_activities_messages.asset_ropa.dpia_request_status as Record<string, string[]>;
  return messageMap[value] ?? value;
};

const lifecycleStatusMessage = (value: string): string | string[] => {
  const messageMap = processing_activities_messages.asset_ropa.dpia_status as Record<string, string[]>;
  return messageMap[value] ?? value;
};

export const buildDpiaIndicationDrawerSection = (): ApaDpiaDrawerFilterSection => ({
  sectionName: processing_activities_messages.asset_ropa.filters.dpia_indication,
  filters: (['true', 'false'] as const).map(value => ({
    id: value,
    name: booleanFilterMessage(value),
  })),
  isSingleSelect: true,
});

export const buildDpiaRequestDrawerSection = (filters: AssetRopaDpiaFilterFields): ApaDpiaDrawerFilterSection => ({
  sectionName: processing_activities_messages.asset_ropa.filters.dpia_request,
  filters: DPIA_REQUEST_STATUS_FILTER_VALUES.map(value => ({
    id: value,
    name: requestStatusMessage(value),
  })),
  isSingleSelect: true,
  isDisabled: !isDpiaIndicationYes(filters.dpiaIndication),
  disabledTooltip: dpiaDependentFiltersDisabledTooltip,
});

export const buildDpiaLifecycleDrawerSection = (filters: AssetRopaDpiaFilterFields): ApaDpiaDrawerFilterSection => ({
  sectionName: processing_activities_messages.asset_ropa.filters.dpia_status_lifecycle,
  filters: DPIA_LIFECYCLE_STATUS_FILTER_VALUES.map(value => ({
    id: value,
    name: lifecycleStatusMessage(value),
  })),
  isSingleSelect: true,
  isDisabled: !isDpiaIndicationYes(filters.dpiaIndication),
  disabledTooltip: dpiaDependentFiltersDisabledTooltip,
});

export const readDpiaIndicationFromTableFilters = (tableFilters: {
  dpia_indication?: ApaDpiaDrawerFilterChip[];
}): AssetRopaDpiaFilterFields['dpiaIndication'] =>
  (tableFilters.dpia_indication?.[0]?.id as AssetRopaDpiaFilterFields['dpiaIndication']) ?? '';

export const readDpiaFiltersFromTableSections = (tableFilters: {
  dpia_indication?: ApaDpiaDrawerFilterChip[];
  dpia_request?: ApaDpiaDrawerFilterChip[];
  dpia_status?: ApaDpiaDrawerFilterChip[];
}): AssetRopaDpiaFilterFields => {
  const dpiaIndication = readDpiaIndicationFromTableFilters(tableFilters);

  if (!isDpiaIndicationYes(dpiaIndication)) {
    return {
      dpiaIndication,
      dpiaRequestStatus: '',
      dpiaStatus: '',
    };
  }

  return {
    dpiaIndication,
    dpiaRequestStatus: (tableFilters.dpia_request?.[0]?.id as AssetRopaDpiaFilterFields['dpiaRequestStatus']) ?? '',
    dpiaStatus: (tableFilters.dpia_status?.[0]?.id as AssetRopaDpiaFilterFields['dpiaStatus']) ?? '',
  };
};

export const writeDpiaFiltersToTableSections = (
  filters: AssetRopaDpiaFilterFields,
): {
  dpia_indication?: ApaDpiaDrawerFilterChip[];
  dpia_request?: ApaDpiaDrawerFilterChip[];
  dpia_status?: ApaDpiaDrawerFilterChip[];
} => {
  const tableFilters: {
    dpia_indication?: ApaDpiaDrawerFilterChip[];
    dpia_request?: ApaDpiaDrawerFilterChip[];
    dpia_status?: ApaDpiaDrawerFilterChip[];
  } = {};

  if (filters.dpiaIndication) {
    tableFilters.dpia_indication = [
      {
        id: filters.dpiaIndication,
        name: booleanFilterMessage(filters.dpiaIndication as 'true' | 'false'),
      },
    ];
  }

  if (isDpiaIndicationYes(filters.dpiaIndication) && filters.dpiaRequestStatus) {
    tableFilters.dpia_request = [
      {
        id: filters.dpiaRequestStatus,
        name: requestStatusMessage(filters.dpiaRequestStatus),
      },
    ];
  }

  if (isDpiaIndicationYes(filters.dpiaIndication) && filters.dpiaStatus) {
    tableFilters.dpia_status = [
      {
        id: filters.dpiaStatus,
        name: lifecycleStatusMessage(filters.dpiaStatus),
      },
    ];
  }

  return tableFilters;
};
